import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Camera, Upload, ArrowLeft, Trash2, GitCompare,
  X, Plus, Scale,
  Calendar, CheckSquare, Square, Info,
  Image as ImageIcon, TrendingDown, TrendingUp
} from 'lucide-react';
import toast from 'react-hot-toast';
import progressPhotosService, {
  ProgressPhoto,
  TimelineDay,
  BodyPart,
  BODY_PART_LABELS,
} from '../services/progressPhotosService';

// ─── helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('ro-RO', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

function formatDateShort(iso: string) {
  return new Date(iso).toLocaleDateString('ro-RO', {
    day: '2-digit', month: 'short',
  });
}

// ─── Upload Modal ────────────────────────────────────────────────────────────

interface UploadModalProps {
  onClose: () => void;
  onUploaded: (photo: ProgressPhoto) => void;
}

function UploadModal({ onClose, onUploaded }: UploadModalProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [bodyPart, setBodyPart] = useState<BodyPart>('FULL_BODY');
  const [takenAt, setTakenAt] = useState(new Date().toISOString().split('T')[0]);
  const [weight, setWeight] = useState('');
  const [bodyFat, setBodyFat] = useState('');
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  // Prevent background scroll when modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleFile = (f: File) => {
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(f);
  };

  const handleUpload = async () => {
    if (!file) return toast.error('Selectează o fotografie mai întâi');
    setUploading(true);
    try {
      const result = await progressPhotosService.uploadPhoto({
        file,
        bodyPart,
        takenAt: new Date(takenAt).toISOString(),
        weight: weight ? parseFloat(weight) : undefined,
        bodyFat: bodyFat ? parseFloat(bodyFat) : undefined,
        notes: notes || undefined,
      });
      toast.success('Fotografie adăugată cu succes! 💪');
      onUploaded(result.photo);
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Eroare la încărcare');
    } finally {
      setUploading(false);
    }
  };

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Sheet */}
      <div className="bg-white w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl flex flex-col"
        style={{ maxHeight: 'calc(100dvh - env(safe-area-inset-top, 0px) - 8px)' }}
      >
        {/* Drag handle (mobile only) */}
        <div className="flex justify-center pt-2 pb-1 sm:hidden">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        {/* Header – sticky inside the sheet */}
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white rounded-t-2xl sm:rounded-t-2xl flex-shrink-0">
          <h2 className="text-base font-bold text-gray-900">Adaugă fotografie progres</h2>
          <button
            onClick={onClose}
            className="p-2 -mr-1 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>

        {/* Scrollable body */}
        <div
          ref={scrollRef}
          className="overflow-y-auto overscroll-contain flex-1"
          style={{ WebkitOverflowScrolling: 'touch' } as React.CSSProperties}
        >
          <div className="p-4 space-y-4 pb-6">

            {/* Photo picker */}
            {!preview ? (
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed border-blue-300 rounded-xl bg-blue-50 active:bg-blue-100 transition-colors"
                >
                  <Camera className="w-7 h-7 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Fă o poză</span>
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="flex flex-col items-center justify-center gap-2 py-5 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  <Upload className="w-7 h-7 text-gray-500" />
                  <span className="text-sm font-medium text-gray-600">Din galerie</span>
                </button>
              </div>
            ) : (
              <div className="relative">
                <img
                  src={preview}
                  alt="Preview"
                  className="w-full h-52 object-cover rounded-xl"
                />
                <button
                  onClick={() => { setPreview(null); setFile(null); }}
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 active:bg-black/80"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Hidden inputs */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />

            {/* Body part */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Zonă corp</label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(BODY_PART_LABELS) as BodyPart[]).map((bp) => (
                  <button
                    key={bp}
                    onClick={() => setBodyPart(bp)}
                    className={`py-2.5 px-3 rounded-xl text-sm font-medium border transition-colors ${
                      bodyPart === bp
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-200 active:border-blue-300 active:bg-blue-50'
                    }`}
                  >
                    {BODY_PART_LABELS[bp]}
                  </button>
                ))}
              </div>
            </div>

            {/* Date */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Data fotografiei</label>
              <input
                type="date"
                value={takenAt}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => setTakenAt(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              />
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">Greutate (kg)</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  placeholder="ex: 75.5"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">% Grăsime</label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  placeholder="ex: 18.5"
                  value={bodyFat}
                  onChange={(e) => setBodyFat(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Note (opțional)</label>
              <textarea
                rows={2}
                placeholder="ex: Săptămâna 4 de antrenament..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none bg-white"
              />
            </div>

          </div>
        </div>

        {/* Submit – always visible at bottom, outside scroll area */}
        <div
          className="px-4 pt-3 pb-4 bg-white border-t border-gray-100 flex-shrink-0"
          style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
        >
          <button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-base disabled:opacity-50 disabled:cursor-not-allowed active:bg-blue-700 transition-colors"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                </svg>
                Se încarcă...
              </span>
            ) : 'Salvează fotografia'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Photo Detail Modal ──────────────────────────────────────────────────────

interface PhotoDetailModalProps {
  photo: ProgressPhoto;
  onClose: () => void;
  onDelete: (id: string) => void;
  onSelectForCompare: (photo: ProgressPhoto) => void;
  isSelectedForCompare: boolean;
}

function PhotoDetailModal({ photo, onClose, onDelete, onSelectForCompare, isSelectedForCompare }: PhotoDetailModalProps) {
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (!confirm('Ești sigur că vrei să ștergi această fotografie?')) return;
    setDeleting(true);
    try {
      await progressPhotosService.deletePhoto(photo.id);
      toast.success('Fotografie ștearsă');
      onDelete(photo.id);
      onClose();
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-sm rounded-2xl overflow-hidden">
        {/* Photo */}
        <div className="relative">
          <img
            src={progressPhotosService.getPhotoUrl(photo.photoUrl)}
            alt="Progress"
            className="w-full h-72 object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-2"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
            {BODY_PART_LABELS[photo.bodyPart]}
          </div>
        </div>

        {/* Info */}
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-900">{formatDate(photo.takenAt)}</span>
            <div className="flex gap-2">
              {photo.weight && (
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                  {photo.weight} kg
                </span>
              )}
              {photo.bodyFat && (
                <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                  {photo.bodyFat}% grăsime
                </span>
              )}
            </div>
          </div>

          {photo.notes && (
            <p className="text-sm text-gray-600 bg-gray-50 rounded-lg p-2">{photo.notes}</p>
          )}

          {/* Actions */}
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button
              onClick={() => { onSelectForCompare(photo); onClose(); }}
              className={`flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium border transition-colors ${
                isSelectedForCompare
                  ? 'bg-green-600 text-white border-green-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300'
              }`}
            >
              {isSelectedForCompare ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
              {isSelectedForCompare ? 'Selectată' : 'Compară'}
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-medium bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 transition-colors disabled:opacity-50"
            >
              <Trash2 className="w-4 h-4" />
              Șterge
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Compare View ────────────────────────────────────────────────────────────

interface CompareViewProps {
  photo1: ProgressPhoto;
  photo2: ProgressPhoto;
  onClose: () => void;
}

function CompareView({ photo1, photo2, onClose }: CompareViewProps) {
  // Ensure photo1 is the older one (before), photo2 is newer (after)
  const before = new Date(photo1.takenAt) <= new Date(photo2.takenAt) ? photo1 : photo2;
  const after = new Date(photo1.takenAt) <= new Date(photo2.takenAt) ? photo2 : photo1;

  const weightDiff = after.weight && before.weight ? after.weight - before.weight : null;
  const bodyFatDiff = after.bodyFat && before.bodyFat ? after.bodyFat - before.bodyFat : null;

  const daysDiff = Math.round(
    (new Date(after.takenAt).getTime() - new Date(before.takenAt).getTime()) / (1000 * 60 * 60 * 24)
  );

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80">
        <button onClick={onClose} className="text-white p-2">
          <X className="w-6 h-6" />
        </button>
        <h2 className="text-white font-bold text-lg">Comparație progres</h2>
        <div className="w-10" />
      </div>

      {/* Photos side by side */}
      <div className="flex flex-1 overflow-hidden">
        {/* Before */}
        <div className="flex-1 relative">
          <img
            src={progressPhotosService.getPhotoUrl(before.photoUrl)}
            alt="Înainte"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <div className="text-white text-xs font-bold uppercase tracking-wider mb-1">ÎNAINTE</div>
            <div className="text-white text-sm">{formatDate(before.takenAt)}</div>
            {before.weight && (
              <div className="text-white/80 text-xs">{before.weight} kg</div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="w-0.5 bg-white/30 relative z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg">
            <GitCompare className="w-4 h-4 text-gray-700" />
          </div>
        </div>

        {/* After */}
        <div className="flex-1 relative">
          <img
            src={progressPhotosService.getPhotoUrl(after.photoUrl)}
            alt="După"
            className="w-full h-full object-cover"
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3">
            <div className="text-white text-xs font-bold uppercase tracking-wider mb-1">DUPĂ</div>
            <div className="text-white text-sm">{formatDate(after.takenAt)}</div>
            {after.weight && (
              <div className="text-white/80 text-xs">{after.weight} kg</div>
            )}
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="bg-gray-900 p-4">
        <div className="flex items-center justify-around">
          <div className="text-center">
            <div className="text-gray-400 text-xs mb-1">Interval</div>
            <div className="text-white font-bold">{daysDiff} zile</div>
          </div>
          {weightDiff !== null && (
            <div className="text-center">
              <div className="text-gray-400 text-xs mb-1">Greutate</div>
              <div className={`font-bold flex items-center gap-1 ${weightDiff < 0 ? 'text-green-400' : 'text-red-400'}`}>
                {weightDiff < 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                {weightDiff > 0 ? '+' : ''}{weightDiff.toFixed(1)} kg
              </div>
            </div>
          )}
          {bodyFatDiff !== null && (
            <div className="text-center">
              <div className="text-gray-400 text-xs mb-1">% Grăsime</div>
              <div className={`font-bold flex items-center gap-1 ${bodyFatDiff < 0 ? 'text-green-400' : 'text-red-400'}`}>
                {bodyFatDiff < 0 ? <TrendingDown className="w-4 h-4" /> : <TrendingUp className="w-4 h-4" />}
                {bodyFatDiff > 0 ? '+' : ''}{bodyFatDiff.toFixed(1)}%
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

type Tab = 'gallery' | 'timeline' | 'compare';

export default function ProgressPhotosPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<Tab>('gallery');
  const [photos, setPhotos] = useState<ProgressPhoto[]>([]);
  const [timeline, setTimeline] = useState<TimelineDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<ProgressPhoto | null>(null);
  const [comparePhotos, setComparePhotos] = useState<ProgressPhoto[]>([]);
  const [showCompare, setShowCompare] = useState(false);
  const [filterBodyPart, setFilterBodyPart] = useState<BodyPart | 'ALL'>('ALL');
  const [totalPhotos, setTotalPhotos] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [photosRes, timelineRes] = await Promise.all([
        progressPhotosService.getPhotos({ limit: 100 }),
        progressPhotosService.getTimeline(),
      ]);
      setPhotos(photosRes.photos);
      setTotalPhotos(photosRes.total);
      setTimeline(timelineRes.timeline);
    } catch (err: any) {
      toast.error('Eroare la încărcarea fotografiilor');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleUploaded = (photo: ProgressPhoto) => {
    setPhotos(prev => [photo, ...prev]);
    setTotalPhotos(prev => prev + 1);
    loadData(); // refresh timeline too
  };

  const handleDeleted = (id: string) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
    setTotalPhotos(prev => prev - 1);
    setComparePhotos(prev => prev.filter(p => p.id !== id));
    loadData();
  };

  const handleSelectForCompare = (photo: ProgressPhoto) => {
    setComparePhotos(prev => {
      const exists = prev.find(p => p.id === photo.id);
      if (exists) return prev.filter(p => p.id !== photo.id);
      if (prev.length >= 2) {
        toast('Poți selecta maxim 2 fotografii pentru comparație', { icon: 'ℹ️' });
        return [prev[1], photo]; // replace oldest selection
      }
      return [...prev, photo];
    });
  };

  const filteredPhotos = filterBodyPart === 'ALL'
    ? photos
    : photos.filter(p => p.bodyPart === filterBodyPart);

  const canCompare = comparePhotos.length === 2;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-lg mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-gray-100">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <div className="flex-1">
            <h1 className="text-lg font-bold text-gray-900">Fotografii Progres</h1>
            <p className="text-xs text-gray-500">{totalPhotos} fotografii</p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-2 rounded-xl text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Adaugă
          </button>
        </div>

        {/* Tabs */}
        <div className="max-w-lg mx-auto px-4 flex gap-1 pb-2">
          {([
            { id: 'gallery', label: 'Galerie', icon: ImageIcon },
            { id: 'timeline', label: 'Istoric', icon: Calendar },
            { id: 'compare', label: 'Compară', icon: GitCompare },
          ] as { id: Tab; label: string; icon: any }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'compare' && comparePhotos.length > 0 && (
                <span className="bg-white/30 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                  {comparePhotos.length}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 pt-4">
        {/* ── GALLERY TAB ── */}
        {activeTab === 'gallery' && (
          <div>
            {/* Body part filter */}
            <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide">
              <button
                onClick={() => setFilterBodyPart('ALL')}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  filterBodyPart === 'ALL'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-200'
                }`}
              >
                Toate
              </button>
              {(Object.keys(BODY_PART_LABELS) as BodyPart[]).map(bp => (
                <button
                  key={bp}
                  onClick={() => setFilterBodyPart(bp)}
                  className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    filterBodyPart === bp
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200'
                  }`}
                >
                  {BODY_PART_LABELS[bp]}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="grid grid-cols-3 gap-1">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="aspect-square bg-gray-200 rounded-lg animate-pulse" />
                ))}
              </div>
            ) : filteredPhotos.length === 0 ? (
              <div className="text-center py-16">
                <Camera className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Nicio fotografie încă</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Adaugă prima ta fotografie de progres și urmărește transformarea!
                </p>
                <button
                  onClick={() => setShowUpload(true)}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  Adaugă prima fotografie
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1">
                {filteredPhotos.map(photo => {
                  const isSelected = comparePhotos.some(p => p.id === photo.id);
                  return (
                    <button
                      key={photo.id}
                      onClick={() => setSelectedPhoto(photo)}
                      className="relative aspect-square rounded-lg overflow-hidden group"
                    >
                      <img
                        src={progressPhotosService.getPhotoUrl(photo.photoUrl)}
                        alt={BODY_PART_LABELS[photo.bodyPart]}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {/* Overlay on hover */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                      {/* Selected indicator */}
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 bg-green-500 rounded-full p-0.5">
                          <CheckSquare className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      {/* Date badge */}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-1.5">
                        <p className="text-white text-[10px] font-medium">{formatDateShort(photo.takenAt)}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── TIMELINE TAB ── */}
        {activeTab === 'timeline' && (
          <div>
            {loading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                    <div className="h-4 bg-gray-200 rounded w-32 mb-3" />
                    <div className="flex gap-2">
                      {Array.from({ length: 3 }).map((_, j) => (
                        <div key={j} className="w-20 h-20 bg-gray-200 rounded-lg" />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : timeline.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Nicio fotografie în istoric</h3>
                <p className="text-gray-500 text-sm">Adaugă fotografii pentru a vedea istoricul progresului tău.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {timeline.map(day => (
                  <div key={day.date} className="bg-white rounded-xl overflow-hidden shadow-sm border border-gray-100">
                    {/* Day header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-blue-500" />
                        <span className="font-semibold text-gray-900 text-sm">{formatDate(day.date)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {day.weight && (
                          <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Scale className="w-3 h-3" />
                            {day.weight} kg
                          </span>
                        )}
                        <span className="text-xs text-gray-400">{day.count} foto</span>
                      </div>
                    </div>

                    {/* Photos row */}
                    <div className="flex gap-2 p-3 overflow-x-auto scrollbar-hide">
                      {day.photos.map(p => (
                        <button
                          key={p.id}
                          onClick={() => {
                            const full = photos.find(ph => ph.id === p.id);
                            if (full) setSelectedPhoto(full);
                          }}
                          className="flex-shrink-0 relative"
                        >
                          <img
                            src={progressPhotosService.getPhotoUrl(p.photoUrl || '')}
                            alt={p.bodyPart ? BODY_PART_LABELS[p.bodyPart as BodyPart] : ''}
                            className="w-20 h-20 object-cover rounded-lg"
                            loading="lazy"
                          />
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 rounded-b-lg px-1 py-0.5">
                            <p className="text-white text-[9px] text-center truncate">
                              {p.bodyPart ? BODY_PART_LABELS[p.bodyPart as BodyPart] : ''}
                            </p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── COMPARE TAB ── */}
        {activeTab === 'compare' && (
          <div>
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex gap-3">
              <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-800">Cum funcționează comparația</p>
                <p className="text-xs text-blue-600 mt-1">
                  Selectează 2 fotografii din galerie (apasă pe ele și alege "Compară"), 
                  apoi apasă butonul de mai jos pentru a le vedea față în față.
                </p>
              </div>
            </div>

            {/* Selected photos */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              {[0, 1].map(idx => (
                <div key={idx} className="bg-white rounded-xl border-2 border-dashed border-gray-200 overflow-hidden">
                  {comparePhotos[idx] ? (
                    <div className="relative">
                      <img
                        src={progressPhotosService.getPhotoUrl(comparePhotos[idx].photoUrl)}
                        alt="Selected"
                        className="w-full h-36 object-cover"
                      />
                      <button
                        onClick={() => setComparePhotos(prev => prev.filter((_, i) => i !== idx))}
                        className="absolute top-1.5 right-1.5 bg-black/50 text-white rounded-full p-1"
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <div className="p-2">
                        <p className="text-xs font-medium text-gray-700">{formatDate(comparePhotos[idx].takenAt)}</p>
                        <p className="text-xs text-gray-500">{BODY_PART_LABELS[comparePhotos[idx].bodyPart]}</p>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="h-36 flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setActiveTab('gallery')}
                    >
                      <Plus className="w-8 h-8 text-gray-300 mb-2" />
                      <p className="text-xs text-gray-400 text-center px-2">
                        {idx === 0 ? 'Fotografie 1' : 'Fotografie 2'}
                      </p>
                      <p className="text-xs text-blue-500 mt-1">Selectează din galerie</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Compare button */}
            <button
              onClick={() => setShowCompare(true)}
              disabled={!canCompare}
              className="w-full py-3.5 bg-blue-600 text-white rounded-xl font-semibold disabled:opacity-40 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              <GitCompare className="w-5 h-5" />
              {canCompare ? 'Compară fotografiile' : `Selectează ${2 - comparePhotos.length} fotografi${2 - comparePhotos.length === 1 ? 'e' : 'i'} din galerie`}
            </button>

            {/* Quick select from gallery */}
            {photos.length > 0 && (
              <div className="mt-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Selectează rapid din galerie</h3>
                <div className="grid grid-cols-3 gap-1">
                  {photos.slice(0, 12).map(photo => {
                    const isSelected = comparePhotos.some(p => p.id === photo.id);
                    return (
                      <button
                        key={photo.id}
                        onClick={() => handleSelectForCompare(photo)}
                        className="relative aspect-square rounded-lg overflow-hidden"
                      >
                        <img
                          src={progressPhotosService.getPhotoUrl(photo.photoUrl)}
                          alt=""
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        {isSelected && (
                          <div className="absolute inset-0 bg-blue-600/40 flex items-center justify-center">
                            <CheckSquare className="w-6 h-6 text-white" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black/50 p-1">
                          <p className="text-white text-[9px] text-center">{formatDateShort(photo.takenAt)}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploaded={handleUploaded}
        />
      )}

      {/* Photo Detail Modal */}
      {selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          onDelete={handleDeleted}
          onSelectForCompare={handleSelectForCompare}
          isSelectedForCompare={comparePhotos.some(p => p.id === selectedPhoto.id)}
        />
      )}

      {/* Compare View */}
      {showCompare && canCompare && (
        <CompareView
          photo1={comparePhotos[0]}
          photo2={comparePhotos[1]}
          onClose={() => setShowCompare(false)}
        />
      )}
    </div>
  );
}
